import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight, Loader2, Eye } from "lucide-react";
import Header from "../../components/landing/Header";
import Footer from "../../components/landing/Footer";
import Seo from "../../components/Seo";
import NewsletterSignup from "../../components/NewsletterSignup";
import { BLOG_POSTS, fmtBlogDate } from "../../content/blogPosts";
import api from "../../lib/api";
import { toCard, wrapTables, unlinkUnpublished, fetchDbArticles } from "../../lib/blogApi";
import { BlogCover } from "../../components/brand/BlogCover";

const NotFound = () => (
  <div className="max-w-3xl mx-auto px-5 lg:px-8 pt-40 pb-24 text-center" data-testid="blog-article-404">
    <p className="font-display text-6xl text-brand-laiton">404</p>
    <h1 className="font-display text-3xl text-brand-greenDeep mt-4">Article introuvable</h1>
    <p className="mt-3 text-brand-green/70">Cet article n'existe pas ou n'est pas encore publié.</p>
    <Link to="/blog" className="mt-6 inline-flex items-center gap-2 font-semibold text-brand-greenDeep" data-testid="blog-404-back"><ArrowLeft className="w-4 h-4" /> Tous les articles</Link>
  </div>
);

const Related = ({ items }) => !items.length ? null : (
  <div className="max-w-5xl mx-auto px-5 lg:px-8 mt-16 mb-4">
    <h2 className="font-display text-2xl font-semibold text-brand-greenDeep mb-5">À lire aussi</h2>
    <div className="grid sm:grid-cols-3 gap-5">
      {items.map((p, i) => (
        <Link key={p.slug} to={`/blog/${p.slug}`} className="group rounded-2xl border border-brand-soft overflow-hidden flex flex-col hover:shadow-md transition-shadow" data-testid={`blog-related-${p.slug}`}>
          <div className="h-32 overflow-hidden"><BlogCover post={p} index={i} size="sm" /></div>
          <div className="p-4">
            <h3 className="font-semibold text-brand-greenDeep text-sm leading-snug">{p.title}</h3>
            <span className="mt-2 inline-flex items-center gap-1 text-xs text-brand-greenDeep font-semibold">Lire <ArrowRight className="w-3.5 h-3.5" /></span>
          </div>
        </Link>
      ))}
    </div>
  </div>
);

export default function DbArticle({ slug }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const preview = params.get("apercu") === "1";
  const [a, setA] = useState(undefined);
  const [live, setLive] = useState(null);
  useEffect(() => {
    window.scrollTo(0, 0); setA(undefined);
    api.get(preview ? `/admin/blog/articles/${slug}` : `/blog/articles/${slug}`).then((r) => setA(r.data)).catch(() => setA(null));
    fetchDbArticles().then((l) => setLive(new Set([...BLOG_POSTS, ...l].map((p) => p.slug))));
  }, [slug, preview]);

  // Liens internes relatifs : navigation sans rechargement
  const onContentClick = (e) => {
    const link = e.target.closest("a");
    const href = link?.getAttribute("href");
    if (href && href.startsWith("/") && !e.metaKey && !e.ctrlKey) { e.preventDefault(); navigate(href); }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="blog-article-page">
      <Header />
      <main>
        {a === undefined && <div className="pt-40 pb-24 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-brand-laiton" /></div>}
        {a === null && <NotFound />}
        {a && (
          <article className="pt-32">
            {preview && (
              <div className="max-w-3xl mx-auto px-5 lg:px-8 mb-6">
                <div className="flex items-center gap-2 bg-brand-graphite text-brand-ivoire text-sm px-4 py-3 rounded-[10px]" data-testid="blog-preview-banner">
                  <Eye className="w-4 h-4 text-brand-laiton" /> Aperçu administrateur · {a.status === "draft" ? "Brouillon" : new Date(a.publish_at) > new Date() ? `Programmé le ${new Date(a.publish_at).toLocaleString("fr-FR", { timeZone: "Europe/Paris", dateStyle: "long", timeStyle: "short" })}` : "En ligne"} · invisible du public tant qu'il n'est pas publié
                </div>
              </div>
            )}
            <Seo title={a.seo_title} description={a.meta_description} path={`/blog/${a.slug}`} jsonLd={a.jsonld} />
            <div className="max-w-3xl mx-auto px-5 lg:px-8">
              <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-medium text-brand-green/70 hover:text-brand-greenDeep transition-colors mb-8" data-testid="blog-back">
                <ArrowLeft className="w-4 h-4" /> Tous les articles
              </Link>
              <p className="text-xs font-semibold text-brand-limeDark uppercase tracking-wide">{a.category}</p>
              <h1 className="font-display text-4xl lg:text-5xl font-semibold text-brand-greenDeep leading-tight mt-3" data-testid="blog-article-title">{a.title}</h1>
              <p className="mt-3 text-sm text-brand-green/55" data-testid="blog-article-byline">Par la rédaction 360immoinvest · Mis à jour le {fmtBlogDate(a.updated_at)}</p>
              {a.summary && (
                <aside className="mt-8 bg-[#F6F2EA] border-l-4 border-[#B8925A] rounded-r-[10px] px-6 py-5" data-testid="blog-article-summary">
                  <p className="text-xs font-semibold uppercase tracking-wide text-[#8a6a3c] mb-2">L'essentiel</p>
                  <p className="text-brand-greenDeep leading-relaxed text-[15px]">{a.summary}</p>
                </aside>
              )}
              <div className="blog-html mt-10" onClick={onContentClick} data-testid="blog-article-content" dangerouslySetInnerHTML={{ __html: wrapTables(live ? unlinkUnpublished(a.content_html, live) : a.content_html) }} />
            </div>
            <div className="max-w-3xl mx-auto px-5 lg:px-8 mt-12"><NewsletterSignup source="blog-article" /></div>
            <div className="max-w-3xl mx-auto px-5 lg:px-8 mt-12">
              <div className="rounded-3xl bg-brand-greenDeep text-white p-8 text-center">
                <h2 className="font-display text-2xl font-semibold">Gérez vos locations sans la paperasse</h2>
                <p className="mt-2 text-white/75 text-[15px]">Quittances, loyers, révision IRL et revenus fonciers au même endroit. Gratuit pour 1 bien.</p>
                <button onClick={() => navigate("/connexion")} className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold" data-testid="blog-cta">
                  Créer mon compte gratuit <ArrowUpRight className="w-5 h-5" />
                </button>
              </div>
            </div>
            <Related items={(a.related || []).map(toCard)} />
          </article>
        )}
        <div className="h-16" />
      </main>
      <Footer />
    </div>
  );
}
